'use client';

import { useEffect, useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Stack,
  Button,
  IconButton,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { motion } from 'framer-motion';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

export interface EmployeeFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  position: string;
  registrationNumber: string;
  assignedCategoryId: number;
  onboardingDate: string;
  startDate: string;
  endDate: string;
  remoteWorkSurfaceArea: number;
  teleworkRentElectricityTaxes: number;
  teleworkConnection: number;
  teleworkOffice: number;

}

type AddEmployeeDrawerProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<EmployeeFormData>) => void;
  editingEmployee?: EmployeeFormData | null;
};

export default function AddEmployeeDrawer({
  open,
  onClose,
  onSubmit,
  editingEmployee,
}: AddEmployeeDrawerProps) {
  const [formData, setFormData] = useState<Partial<EmployeeFormData>>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: '',
    registrationNumber: '',
    assignedCategoryId: 0,
    onboardingDate: '',
    startDate: '',
    endDate: '',
    remoteWorkSurfaceArea: 0,
    teleworkRentElectricityTaxes: 100,
    teleworkConnection: 100,
    teleworkOffice: 100,
  });
  const [categories, setCategories] = useState<{ id: number; name: string }[]>([]);

  useEffect(() => {
    if (editingEmployee) {
      setFormData(editingEmployee);
    } else {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        position: '',
        registrationNumber: '',
        assignedCategoryId: 0,
        onboardingDate: '',
        startDate: '',
        endDate: '',
        remoteWorkSurfaceArea: 0,
        teleworkRentElectricityTaxes: 100,
        teleworkConnection: 100,
        teleworkOffice: 100,
      });
    }
  }, [editingEmployee]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const apiKey = localStorage.getItem('apiKey');
        if (!apiKey) return;

        const res = await axios.get(
          'https://staging-third-party-apis.nabil5332.workers.dev/api/v1/categories',
          { headers: { Authorization: `Bearer ${apiKey}` } }
        );

        setCategories(res.data.categories || []);

        if (!editingEmployee && res.data.categories.length > 0) {
          setFormData((prev) => ({
            ...prev,
            assignedCategoryId: res.data.categories[0].id,
          }));
        }
      } catch (err) {
        console.error('Erreur lors du chargement des catégories', err);
      }
    };
    fetchCategories();
  }, [editingEmployee]);

  const handleChange = (field: keyof EmployeeFormData, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: typeof prev[field] === 'number' ? Number(value) : value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.assignedCategoryId) {
      alert('Veuillez remplir tous les champs obligatoires.');
      return;
    }

    try {
      const apiKey = localStorage.getItem('apiKey');
      if (!apiKey) throw new Error('API Key manquante dans localStorage');

      const idempotencyKey = uuidv4();

      // Payload final
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        position: formData.position,
        registrationNumber: formData.registrationNumber,
        assignedCategoryId: formData.assignedCategoryId,
        onboardingDate: formData.onboardingDate || undefined,
        startDate: formData.startDate || undefined,
        endDate: formData.endDate || undefined,
        remoteWorkSurfaceArea: formData.remoteWorkSurfaceArea || 0,
        rentUtilitiesTaxesPercentage: Number(formData.teleworkRentElectricityTaxes || 0),
        internetConnectionPercentage: Number(formData.teleworkConnection || 0),
        officeEquipmentPercentage: Number(formData.teleworkOffice || 0),
      } as any;


      await axios.post(
        'https://staging-third-party-apis.nabil5332.workers.dev/api/v1/employees',
        payload,
        {
          headers: {
            Authorization: `Bearer ${apiKey}`,
            'Content-Type': 'application/json',
            'Idempotency-Key': idempotencyKey,
          },
        }
      );

      onSubmit(formData);
      onClose();
      setTimeout(() => window.location.reload(), 500);

    } catch (err: any) {
      console.error('Erreur lors de la création de l’employé :', err.response?.data || err);
    }
  };



  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: 450, p: 4, borderRadius: '16px 0 0 16px' },
      }}
    >
      <motion.div
        initial={{ x: 300, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 300, opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6" sx={{ color: '#714291', fontWeight: 600 }}>
            {editingEmployee ? 'Modifier un employé' : 'Nouvel employé'}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box display="flex" flexDirection="column" gap={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="First Name"
              value={formData.firstName ?? ''}
              onChange={(e) => handleChange('firstName', e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Last Name"
              value={formData.lastName ?? ''}
              onChange={(e) => handleChange('lastName', e.target.value)}
              fullWidth
              size="small"
            />
          </Stack>

          <TextField
            label="Email"
            type="email"
            value={formData.email ?? ''}
            onChange={(e) => handleChange('email', e.target.value)}
            fullWidth
            size="small"
          />

          <TextField
            label="Phone"
            value={formData.phone ?? ''}
            onChange={(e) => handleChange('phone', e.target.value)}
            fullWidth
            size="small"
          />

          <TextField
            label="Position"
            value={formData.position ?? ''}
            onChange={(e) => handleChange('position', e.target.value)}
            fullWidth
            size="small"
          />

          <TextField
            label="Registration Number"
            value={formData.registrationNumber ?? ''}
            onChange={(e) => handleChange('registrationNumber', e.target.value)}
            fullWidth
            size="small"
          />

          <TextField
            select
            label="Category"
            value={formData.assignedCategoryId ?? ''}
            onChange={(e) => handleChange('assignedCategoryId', Number(e.target.value))}
            fullWidth
            size="small"
          >
            {categories.map((cat) => (
              <MenuItem key={cat.id} value={cat.id}>
                {cat.name}
              </MenuItem>
            ))}
          </TextField>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Onboarding Date"
              type="date"
              value={formData.onboardingDate ?? ''}
              onChange={(e) => handleChange('onboardingDate', e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Start Date"
              type="date"
              value={formData.startDate ?? ''}
              onChange={(e) => handleChange('startDate', e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Stack>

          <TextField
            label="End Date"
            type="date"
            value={formData.endDate ?? ''}
            onChange={(e) => handleChange('endDate', e.target.value)}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Remote Work Surface Area"
            type="number"
            value={formData.remoteWorkSurfaceArea ?? 0}
            onChange={(e) => handleChange('remoteWorkSurfaceArea', e.target.value)}
            fullWidth
            size="small"
          />

          {/* ✅ Section Télétravail */}
          <Typography variant="subtitle2" sx={{ mt: 3, fontWeight: 600 }}>
            Informations Télétravail
          </Typography>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Loyer / Électricité / Taxes (%)"
              type="number"
              value={formData.teleworkRentElectricityTaxes ?? 0}
              onChange={(e) => handleChange('teleworkRentElectricityTaxes', e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Connexion (%)"
              type="number"
              value={formData.teleworkConnection ?? 0}
              onChange={(e) => handleChange('teleworkConnection', e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Bureautique (%)"
              type="number"
              value={formData.teleworkOffice ?? 0}
              onChange={(e) => handleChange('teleworkOffice', e.target.value)}
              fullWidth
              size="small"
            />
          </Stack>

          <Stack direction="row" spacing={2} justifyContent="flex-end" mt={2}>
            <Button variant="outlined" color="inherit" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{
                py: 1.2,
                borderRadius: 3,
                backgroundColor: '#714291',
                '&:hover': { backgroundColor: '#5a336f' },
              }}
            >
              {editingEmployee ? 'Enregistrer' : 'Créer'}
            </Button>
          </Stack>
        </Box>
      </motion.div>
    </Drawer>
  );
}
